import React, { useState } from 'react';

import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FolderIcon from '@mui/icons-material/Folder';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

export type TreeNode = {
  /** ファイル・ディレクトリ名 */
  name: string;
  /** 説明テキスト */
  desc?: string;
  /** カテゴリカラー（ディレクトリに色付け） */
  color?: string;
  /** ファイル単位のハイライトカラー（名前・説明テキストに適用） */
  highlight?: string;
  /** 子ノード（存在する場合はディレクトリとして扱う） */
  children?: TreeNode[];
  /** 初期状態で開いているか（デフォルト: false） */
  defaultOpen?: boolean;
  /** ファイル選択用ID（設定されているファイルのみクリック可能） */
  fileId?: string;
};

type Props = {
  /** ルートディレクトリ名（ヘッダー表示） */
  root: string;
  /** ツリーエントリ一覧 */
  entries: TreeNode[];
  /** ファイル選択時のコールバック（fileIdを通知） */
  onFileSelect?: (fileId: string) => void;
  /** 現在選択中のfileId（ハイライト表示用） */
  selectedFileId?: string;
};

/**
 * 拡張子からファイルアイコンとカラーを返す
 * 新しい拡張子に対応する場合はここにエントリを追加する
 */
const getFileIcon = (
  fileName: string,
  fallbackColor: string,
): { icon: React.ComponentType<{ sx?: object }>; color: string } => {
  const ext = fileName.split('.').pop()?.toLowerCase();
  switch (ext) {
    case 'md':
      return { icon: DescriptionOutlinedIcon, color: '#42a5f5' };
    default:
      return { icon: InsertDriveFileOutlinedIcon, color: fallbackColor || '' };
  }
};

/**
 * 再帰的にツリーノードを描画するコンポーネント
 */
const TreeNodeItem: React.FC<{
  node: TreeNode;
  depth: number;
  parentColor?: string;
  onFileSelect?: (fileId: string) => void;
  selectedFileId?: string;
}> = ({ node, depth, parentColor, onFileSelect, selectedFileId }) => {
  const isDir = node.children != null;
  const isClickableFile = !isDir && !!node.fileId;
  const isSelected = isClickableFile && node.fileId === selectedFileId;
  const [open, setOpen] = useState(node.defaultOpen ?? true);
  const color = node.color ?? parentColor;

  const handleClick = () => {
    if (isDir) {
      setOpen((prev) => !prev);
    } else if (isClickableFile && onFileSelect) {
      onFileSelect(node.fileId!);
    }
  };

  return (
    <Box>
      {/* ノード行 */}
      <ButtonBase
        disableRipple={!isDir && !isClickableFile}
        onClick={handleClick}
        sx={{
          display: 'flex',
          alignItems: 'center',
          width: '100%',
          textAlign: 'left',
          justifyContent: 'flex-start',
          pl: depth * 2 + 1,
          pr: 1.5,
          py: 0.4,
          gap: 0.5,
          borderRadius: 1,
          cursor: isDir || isClickableFile ? 'pointer' : 'default',
          transition: 'background-color 0.15s',
          bgcolor: isSelected ? 'action.selected' : 'transparent',
          '&:hover': isDir || isClickableFile ? { bgcolor: 'action.hover' } : {},
        }}
      >
        {/* 展開アイコン（ディレクトリのみ） */}
        {isDir ? (
          open ? (
            <ExpandMoreIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
          ) : (
            <ChevronRightIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
          )
        ) : (
          <Box sx={{ width: 16 }} />
        )}

        {/* ファイル/フォルダアイコン */}
        {isDir ? (
          open ? (
            <FolderOpenIcon sx={{ fontSize: 16, color: color ?? 'text.secondary' }} />
          ) : (
            <FolderIcon sx={{ fontSize: 16, color: color ?? 'text.secondary' }} />
          )
        ) : (
          (() => {
            const { icon: FileIcon, color: iconColor } = getFileIcon(
              node.name,
              color ? `${color}99` : '',
            );
            return <FileIcon sx={{ fontSize: 14, color: iconColor || 'text.secondary' }} />;
          })()
        )}

        {/* ノード名 */}
        <Typography
          variant="caption"
          sx={{
            fontFamily: 'monospace',
            fontWeight: isDir ? 600 : node.highlight ? 600 : 400,
            fontSize: '0.75rem',
            color: node.highlight
              ? node.highlight
              : isDir
                ? (color ?? 'text.primary')
                : 'text.primary',
          }}
        >
          {node.name}
          {isDir && '/'}
        </Typography>

        {/* 説明テキスト */}
        {node.desc && (
          <Typography
            variant="caption"
            sx={{
              color: 'text.secondary',
              fontSize: '0.65rem',
              ml: 0.5,
            }}
          >
            {node.desc}
          </Typography>
        )}
      </ButtonBase>

      {/* 子ノード（開いている場合のみ描画） */}
      {isDir && open && (
        <Box
          sx={{
            borderLeft: 1,
            borderColor: color ? `${color}40` : 'divider',
            ml: depth * 2 + 2,
          }}
        >
          {node.children!.map((child) => (
            <TreeNodeItem
              key={child.name}
              node={child}
              depth={0}
              parentColor={color}
              onFileSelect={onFileSelect}
              selectedFileId={selectedFileId}
            />
          ))}
        </Box>
      )}
    </Box>
  );
};

/**
 * ディレクトリツリー表示コンポーネント（IDE風開閉式）
 *
 * カテゴリ別にカラーアクセントで色分けし、
 * フォルダをクリックして開閉できるエクスプローラー風UI。
 */
const WorkflowDirectoryTree: React.FC<Props> = ({
  root,
  entries,
  onFileSelect,
  selectedFileId,
}) => {
  return (
    <Box
      sx={{
        border: 1,
        borderColor: 'divider',
        borderRadius: 2,
        overflow: 'hidden',
      }}
    >
      {/* ヘッダー */}
      <Box
        sx={{
          px: 2,
          py: 1,
          bgcolor: (theme) =>
            theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.02)',
          borderBottom: 1,
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}
      >
        <FolderIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
        <Typography variant="body2" fontWeight={600} sx={{ fontFamily: 'monospace' }}>
          {root}
        </Typography>
      </Box>
      {/* ツリー本体 */}
      <Stack spacing={0} sx={{ py: 0.75 }}>
        {entries.map((entry) => (
          <TreeNodeItem
            key={entry.name}
            node={entry}
            depth={0}
            onFileSelect={onFileSelect}
            selectedFileId={selectedFileId}
          />
        ))}
      </Stack>
    </Box>
  );
};

export default WorkflowDirectoryTree;
